import { defineComponent as _defineComponent } from 'vue'

import {
  computed,
  defineComponent,
  onBeforeUnmount,
  onMounted,
  ref,
  ssrRef,
  useAsync,
  useContext,
  useMeta,
  useRoute,
  useStore,
} from "@nuxtjs/composition-api";

import { useAmplitude, useMetaService, useTranslate } from "@/composables";
import { useCreatorShop } from "@/store/creator-shop";
import { getProductSetsStore } from "@/store/product-sets";
import { getProfilesStore } from "@/store/profiles";
import { formatErrorMessage, handleErrorStatusCode, handleServiceError } from "@/utils/api";
import logger, { logLevel } from "@/utils/logger";

const __default__ = defineComponent({
  name: "ProductSet",
  head: {},
});

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props) {

const context = useContext();
const amplitude = useAmplitude();
const store = useStore();
const route = useRoute();
const { t } = useTranslate();
const metaService = useMetaService();

const productSetStore = getProductSetsStore(store);
const profilesStore = getProfilesStore(store);
const creatorShop = useCreatorShop();

const displayName = ref(route.value.params.displayName);
const _id = ref(route.value.params.id);

const linksText = t("Paid links"); // Text at the top of a creator's product set page to indicate that the links are paid

const paginate = ssrRef(false, "paginate");
const pending = ssrRef(false, "pending");
const error = ref<unknown | null>(null);
const ltkFetchState = computed(() => ({ error: error.value, pending: pending.value }));

useAsync(async () => {
  try {
    pending.value = true;
    if (
      productSetStore.productSets[route.value.params.id] === undefined ||
      productSetStore.feeds[_id.value] === undefined
    ) {
      await Promise.all([
        productSetStore.fetchProductSet({ id: route.value.params.id }),
        productSetStore.fetchProductSetProducts({ id: route.value.params.id }),
      ]);
    }
    if (productSetStore.productSets[route.value.params.id]?.subtype === "GIFT_GUIDE") {
      pending.value = false;
      return context.redirect({
        name: `explore-displayName-giftguides-id`,
        params: { displayName: route.value.params.displayName, id: route.value.params.id },
      });
    }

    if (!profilesStore.hasProfileBeenFetchedByName(route.value.params.displayName)) {
      await profilesStore.fetchProfileByDisplayName(route.value.params.displayName);
    }
    pending.value = false;
  } catch (err) {
    logger(logLevel.ERROR, "Error fetching product sets", {}, err);
    context.error({
      statusCode: handleErrorStatusCode(err),
      message: `Error fetching product sets (${route.value.params.id}) - ${formatErrorMessage(err)}`,
    });
    error.value = err;
    pending.value = false;
  }
  paginate.value = true;
}, "fetch");

const fetch = async () => {
  if (paginate.value === true || !productSetStore.hasFeedBeenFetched(_id.value)) {
    try {
      pending.value = true;
      await productSetStore.fetchProductSetProducts({ id: _id.value });
      pending.value = false;
    } catch (err) {
      error.value = err;
      pending.value = false;
      return handleServiceError({
        context,
        error: {
          status: handleErrorStatusCode(err),
          errorCode: "product-set-products-not-found",
          developerMessage: `Error fetching product set items (${_id.value})`,
        },
        originalError: err,
      });
    }
  }
  paginate.value = true;
};

onMounted(() => {
  creatorShop.setReferringShopTab();
  amplitude?.sendEvent("/page/product-set-details", productSetEventProps.value);
});

onBeforeUnmount(() => {
  creatorShop.completeShopTabJourney();
});

const onPaging = () => {
  fetch();
};

const productSetEventProps = computed(() => {
  return {
    "gift-guide": false,
    "is-gift-guide": false,
    "product-set-id": _id.value,
    "publisher-id": profileId.value,
    title: productSetName.value,
    "product-set-subtype": "PRODUCT_SET",
  };
});

const productSetName = computed(() => {
  return getProductSetsStore(store).productSets[_id.value]?.title ?? "";
});

const productSetDescription = computed(() => {
  return getProductSetsStore(store).productSets[_id.value]?.description;
});

const productSetProductIds = computed(() => {
  return getProductSetsStore(store).feeds[_id.value]?.ids ?? [];
});

const hasNextPage = computed(() => {
  const productSetStore = getProductSetsStore(store);
  return productSetStore.getFeedHasMorePages(_id.value);
});

const profileId = computed(() => {
  const profilesStore = getProfilesStore(store);
  return profilesStore.getIdByDisplayName(displayName.value);
});

const pageMeta = computed(() => {
  const titleProductSetLength = 28;
  const descriptionProductSetNameLength = 70;
  // strip punctuation at the end of product set name
  const prodtSetName = productSetName.value.replace(/\s*[.,/#!$%^&*;:{}=\-_`~()]\s*$/, "");

  return {
    title: t("{displayName}'s {productSetName} Product Set on LTK", {
      displayName: displayName.value,
      productSetName: metaService.truncateString(prodtSetName, titleProductSetLength),
    }).value,
    description: t("Shop the curated items in this {productSetName} Product Set from {displayName} on LTK!", {
      displayName: displayName.value,
      productSetName: metaService.truncateString(prodtSetName, descriptionProductSetNameLength),
    }).value,
  };
});

const profileAvatar = computed(() => {
  const profilesStore = getProfilesStore(store);
  return profilesStore.profiles[profileId.value ?? ""]?.avatarUrl;
});

useMeta(() => {
  const head = metaService.setPageMeta({
    title: pageMeta.value.title,
    customTitle: true,
    description: pageMeta.value.description,
  });

  if (profileAvatar.value) {
    const height = 400;
    const width = 400;
    const image = `${profileAvatar.value}?fit=crop&w=${width}&h=${height}`;
    head.meta?.push(...context.$metaService.setOGImg(image, width));
  }

  return head;
});

return { __sfc: true,context, amplitude, store, route, t, metaService, productSetStore, profilesStore, creatorShop, displayName, _id, linksText, paginate, pending, error, ltkFetchState, fetch, onPaging, productSetEventProps, productSetName, productSetDescription, productSetProductIds, hasNextPage, profileId, pageMeta, profileAvatar }
}

})